import React, { useContext, useMemo, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Subscribe } from 'unstated';

import { Text } from '@atlaskit/primitives/compiled/text';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts/types';
import { useTeams } from '@atlassian/global-shortcuts/teams';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { CoreInvitesSource } from '@confluence/core-invites-provider/entry-points/constants.ts';
import { EnablementTouchpointEnum } from '@atlassian/expansion-services/services';

import { CreateTeamModal } from '../apps/TeamsAppShortcutItem/TeamAppShortcutItemCreateTeamDialog.tsx';

import { useFallbackUrls } from './useFallbackUrls.tsx';

const ACTION_SUBJECT_ID = 'teamsAppShortcut';
const CREATE_A_TEAM = 'createATeam';
const INVITE_PEOPLE = 'invitePeople';

export const useTeamsAppShortcut = (): {
	config: GlobalShortcutsItem;
	component: React.ReactNode;
} => {
	const hasFiredExposure = useRef<boolean>(false);
	const fallbackUrls = useFallbackUrls();
	const shortcutUrl = fallbackUrls['teamsShortcut'];
	const teamsShortcut = useTeams(shortcutUrl);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const { openCoreInvites } = useContext(CoreInvitesContext);

	const [isCreateTeamModalOpen, setCreateTeamModalOpen] = useState(false);

	const config = useMemo(
		() => ({
			...teamsShortcut,
			onMouseEnter: () => {
				if (!hasFiredExposure.current) {
					hasFiredExposure.current = true;
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'globalNavigation',
							action: 'mouseEnter',
							actionSubject: 'navigationItem',
							actionSubjectId: ACTION_SUBJECT_ID,
						},
					}).fire();
				}
			},
			actions: [
				...(openCoreInvites
					? [
							{
								onClick: () => {
									openCoreInvites(
										CoreInvitesSource.PEOPLE_MENU,
										{
											onModalClosed: () => null,
											onInviteCompleted: () => null,
										},
										undefined,
										{
											resourceAri: `ari:cloud:confluence::site/${cloudId}`,
											source: ACTION_SUBJECT_ID,
										},
										EnablementTouchpointEnum.NAV4_TEAMS_ADD,
									);
								},
								component: <Text>{formatMessage(messages.invitePeople)}</Text>,
								itemId: INVITE_PEOPLE,
							},
						]
					: []),
				{
					onClick: () => {
						setCreateTeamModalOpen(true);
					},
					component: <Text>{formatMessage(messages.createTeam)}</Text>,
					itemId: CREATE_A_TEAM,
				},
			],
		}),
		[
			createAnalyticsEvent,
			formatMessage,
			setCreateTeamModalOpen,
			openCoreInvites,
			cloudId,
			teamsShortcut,
		],
	);

	const createTeamModalComponent = useMemo(() => {
		return (
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => (
					<CreateTeamModal
						isOpen={isCreateTeamModalOpen}
						onClose={() => setCreateTeamModalOpen(false)}
						addFlag={flags.showFlag}
					/>
				)}
			</Subscribe>
		);
	}, [isCreateTeamModalOpen, setCreateTeamModalOpen]);

	return {
		config,
		component: createTeamModalComponent,
	};
};

const messages = defineMessages({
	invitePeople: {
		id: 'side-navigation.custom.teams-app-shortcut.invite-people',
		defaultMessage: 'Invite people',
		description: 'Label for the menu item that opens the invite people modal',
	},
	createTeam: {
		id: 'side-navigation.custom.teams-app-shortcut.create-team',
		defaultMessage: 'Create team',
		description: 'Label for the menu item that opens the create team modal',
	},
});
